import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Alert } from '@/components/ui/alert';
import { AlertDescription } from '@/components/ui/alert-description';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertCircle, Loader2 } from 'lucide-react';

interface LoginFormProps {
  onSubmit: (email: string, password: string) => Promise<void>;
  isLoading?: boolean;
}

const DEMO_ACCOUNTS = [
  {
    label: 'Admin Account',
    email: 'admin@flexhelp.com',
    password: 'admin123',
  },
  {
    label: 'Property Manager Account',
    email: 'manager@flexhelp.com',
    password: 'manager123',
  },
  {
    label: 'Property Owner Account',
    email: 'owner@flexhelp.com',
    password: 'owner123',
  },
  {
    label: 'FlexHelper Account',
    email: 'helper@flexhelp.com',
    password: 'helper123',
  },
];

export function LoginForm({ onSubmit, isLoading }: LoginFormProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>();
  const navigate = useNavigate();

  const handleDemoAccountSelect = (accountEmail: string) => {
    const account = DEMO_ACCOUNTS.find(acc => acc.email === accountEmail);
    if (account) {
      setEmail(account.email);
      setPassword(account.password);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(undefined);

    try {
      await onSubmit(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to login');
    }
  };

  return (
    <Card variant="glass" className="w-full max-w-md backdrop-blur-md border-opacity-20">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl">Welcome back</CardTitle>
        <CardDescription>
          Sign in to your account to continue
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label className="text-foreground/80">Demo Account</Label>
            <Select onValueChange={handleDemoAccountSelect}>
              <SelectTrigger className="bg-background/50 backdrop-blur-sm">
                <SelectValue placeholder="Select a demo account" />
              </SelectTrigger>
              <SelectContent>
                {DEMO_ACCOUNTS.map((account) => (
                  <SelectItem key={account.email} value={account.email}>
                    {account.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-foreground/80">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
              className="bg-background/50 backdrop-blur-sm"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-foreground/80">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              className="bg-background/50 backdrop-blur-sm"
            />
          </div>

          <Button
            type="button"
            variant="link"
            className="px-0 text-primary hover:text-primary/80"
            onClick={() => navigate('/forgot-password')}
            disabled={isLoading}
          >
            Forgot password?
          </Button>
        </CardContent>

        <CardFooter className="flex flex-col gap-4">
          <Button
            type="submit"
            className="w-full transition-all"
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Sign In
          </Button>
          <Button
            type="button"
            variant="glass"
            className="w-full"
            onClick={() => navigate('/register')}
            disabled={isLoading}
          >
            Create an account
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}